import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { GraduationCap, CheckCircle2, AlertCircle, Loader2, Mic, Square, RefreshCw, CalendarClock, Plus, Trash2 } from "lucide-react";
import axios from "axios";
import { Tx } from "../context/AutoTranslateContext";
import { useLanguage } from "../context/LanguageContext";
import WhatsAppOptIn from "../components/WhatsAppOptIn";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOCAL_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
const DAY_LABELS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const SPECIALITIES = ["Tajweed","Hifz","Ijāza","Qirā'āt","Arabic Grammar","Beginner Arabic","Tafsir"];
const LANGUAGES = ["English","Arabic","Urdu","French","German","Turkish","Indonesian","Malay"];

const blobToB64 = (blob) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onloadend = () => res(r.result.split(",")[1]);
  r.onerror = rej;
  r.readAsDataURL(blob);
});

const TutorApply = () => {
  const { lang } = useLanguage();
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", country: "",
    ijaza: "", bio: "", experience_years: 3, photo_url: "",
  });
  const [specs, setSpecs] = useState(new Set(["Tajweed"]));
  const [langs, setLangs] = useState(new Set(["English","Arabic"]));
  const [slots, setSlots] = useState([]);
  const [slotDay, setSlotDay] = useState(1);
  const [slotTime, setSlotTime] = useState("17:00");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState("");
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const toggle = (setter, set, v) => {
    const next = new Set(set); next.has(v) ? next.delete(v) : next.add(v); setter(next);
  };
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const addSlot = () => {
    const key = `${slotDay}-${slotTime}`;
    if (slots.some((s) => `${s.weekday}-${s.local_time}` === key)) return;
    setSlots([...slots, { weekday: Number(slotDay), local_time: slotTime }]);
  };
  const removeSlot = (i) => setSlots(slots.filter((_, idx) => idx !== i));

  const startRec = async () => {
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setAudioBlob(blob); setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((t) => t.stop());
      };
      mediaRef.current = mr; mr.start(); setRecording(true);
    } catch (e) {
      setError("Microphone access denied. Please allow microphone access and try again.");
    }
  };
  const stopRec = () => { if (mediaRef.current) mediaRef.current.stop(); setRecording(false); };
  const clearRec = () => { setAudioBlob(null); setAudioUrl(""); };

  const submit = async (e) => {
    e.preventDefault();
    if (!audioBlob) { setError("Please record a short recitation before submitting."); return; }
    setBusy(true); setError("");
    try {
      const b64 = await blobToB64(audioBlob);
      await axios.post(`${API}/tutoring/apply`, {
        ...form,
        experience_years: Number(form.experience_years),
        specialities: [...specs], languages: [...langs],
        preferred_lang: lang,
        slots: slots.map((s) => ({ weekday: s.weekday, local_time: s.local_time, tz: LOCAL_TZ })),
        recitation_audio_b64: b64,
      });
      setDone(true);
    } catch (e) {
      setError(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  if (done) return (
    <div data-testid="tutor-apply-done" className="max-w-md mx-auto px-4 py-24 text-center">
      <CheckCircle2 className="w-12 h-12 text-forest mx-auto mb-4" />
      <h1 className="font-heading text-2xl text-forest font-bold mb-3"><Tx>Application received</Tx></h1>
      <p className="text-sm text-mute mb-6">
        <Tx>JazākAllāhu khayran. Our team has been notified on WhatsApp and will review your application and recitation within 3 working days.</Tx>
      </p>
      <div className="max-w-md mx-auto"><WhatsAppOptIn /></div>
      <div className="mt-6">
        <Link to="/academy/tutors" className="text-forest hover:underline"><Tx>← Back to tutors</Tx></Link>
      </div>
    </div>
  );

  return (
    <div data-testid="tutor-apply-page" className="max-w-2xl mx-auto px-4 sm:px-8 py-12">
      <div className="flex items-center gap-2 text-xs text-mute mb-6">
        <Link to="/academy" className="hover:text-forest"><Tx>Academy</Tx></Link><span>/</span>
        <Link to="/academy/tutors" className="hover:text-forest"><Tx>Tutors</Tx></Link><span>/</span>
        <span className="text-forest"><Tx>Apply</Tx></span>
      </div>

      <div className="text-center mb-8">
        <GraduationCap className="w-10 h-10 text-gold mx-auto mb-3" />
        <h1 className="font-heading text-4xl text-forest font-bold mb-3"><Tx>Become a tutor</Tx></h1>
        <p className="text-sm text-mute leading-relaxed max-w-lg mx-auto">
          <Tx>Your application is sent to our management team on WhatsApp — you'll only appear in the app once approved. You do NOT set prices: management pays approved tutors a fixed monthly salary out of student subscriptions.</Tx>
        </p>
      </div>

      <form onSubmit={submit} className="clay-surface rounded-3xl p-6 space-y-4">
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block"><span className="text-[10px] uppercase tracking-widest text-mute"><Tx>Full name</Tx></span>
            <input data-testid="apply-name" required minLength={2} value={form.full_name} onChange={upd("full_name")} className="input" /></label>
          <label className="block"><span className="text-[10px] uppercase tracking-widest text-mute"><Tx>Country</Tx></span>
            <input data-testid="apply-country" required value={form.country} onChange={upd("country")} className="input" /></label>
          <label className="block"><span className="text-[10px] uppercase tracking-widest text-mute"><Tx>Email</Tx></span>
            <input data-testid="apply-email" type="email" required value={form.email} onChange={upd("email")} className="input" /></label>
          <label className="block"><span className="text-[10px] uppercase tracking-widest text-mute"><Tx>Phone / WhatsApp</Tx></span>
            <input data-testid="apply-phone" required value={form.phone} onChange={upd("phone")} className="input" /></label>
          <label className="block"><span className="text-[10px] uppercase tracking-widest text-mute"><Tx>Years of experience</Tx></span>
            <input data-testid="apply-years" type="number" min={0} max={80} required value={form.experience_years} onChange={upd("experience_years")} className="input" /></label>
          <label className="block"><span className="text-[10px] uppercase tracking-widest text-mute"><Tx>Photo URL (optional)</Tx></span>
            <input data-testid="apply-photo" type="url" value={form.photo_url} onChange={upd("photo_url")} className="input" /></label>
        </div>

        <label className="block"><span className="text-[10px] uppercase tracking-widest text-mute"><Tx>Ijāza / qualifications (optional)</Tx></span>
          <input data-testid="apply-ijaza" value={form.ijaza} onChange={upd("ijaza")} className="input" /></label>

        <label className="block"><span className="text-[10px] uppercase tracking-widest text-mute"><Tx>Short bio</Tx> ({form.bio.length}/1200)</span>
          <textarea data-testid="apply-bio" required minLength={20} maxLength={1200} rows={5} value={form.bio} onChange={upd("bio")} className="input !rounded-2xl" /></label>

        <div><span className="text-[10px] uppercase tracking-widest text-gold mb-2 block"><Tx>Specialities</Tx></span>
          <div className="flex flex-wrap gap-2">{SPECIALITIES.map((s) => (
            <button type="button" key={s} data-testid={`spec-${s}`} onClick={() => toggle(setSpecs, specs, s)}
              className={`text-xs px-3 py-1.5 rounded-full transition ${specs.has(s) ? "bg-forest text-sand" : "bg-cream text-mute hover:text-forest"}`}>{s}</button>))}</div></div>
        <div><span className="text-[10px] uppercase tracking-widest text-gold mb-2 block"><Tx>Languages spoken</Tx></span>
          <div className="flex flex-wrap gap-2">{LANGUAGES.map((l) => (
            <button type="button" key={l} data-testid={`lang-${l}`} onClick={() => toggle(setLangs, langs, l)}
              className={`text-xs px-3 py-1.5 rounded-full transition ${langs.has(l) ? "bg-forest text-sand" : "bg-cream text-mute hover:text-forest"}`}>{l}</button>))}</div></div>

        {/* Weekly availability — set in the tutor's OWN timezone, auto-converted for students */}
        <div data-testid="apply-availability" className="border-2 border-forest/15 rounded-2xl p-5">
          <p className="text-[10px] uppercase tracking-widest text-gold mb-1 flex items-center gap-1.5">
            <CalendarClock className="w-3.5 h-3.5" /> <Tx>Your weekly availability</Tx>
          </p>
          <p className="text-xs text-mute mb-3">
            <Tx>Set the times you can teach, in YOUR local timezone. Students see these automatically converted to their own timezone.</Tx>
            {" "}<strong>{LOCAL_TZ}</strong>
          </p>
          <div className="flex flex-wrap items-end gap-2 mb-3">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-mute"><Tx>Day</Tx></span>
              <select data-testid="slot-day" value={slotDay} onChange={(e) => setSlotDay(e.target.value)}
                className="mt-1 px-3 py-2 rounded-full bg-cream border border-forest/10 text-sm outline-none focus:border-forest">
                {DAY_LABELS.map((d, i) => <option key={i} value={i}>{d}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-mute"><Tx>Time</Tx></span>
              <input data-testid="slot-time" type="time" value={slotTime} onChange={(e) => setSlotTime(e.target.value)}
                className="mt-1 px-3 py-2 rounded-full bg-cream border border-forest/10 text-sm outline-none focus:border-forest" />
            </label>
            <button type="button" data-testid="slot-add" onClick={addSlot}
              className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-forest text-sand text-xs hover:bg-sage">
              <Plus className="w-3.5 h-3.5" /> <Tx>Add slot</Tx>
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {slots.length === 0 && <span className="text-[11px] text-mute italic"><Tx>No slots added yet (optional, but recommended).</Tx></span>}
            {slots.map((s, i) => (
              <span key={i} data-testid={`slot-chip-${i}`} className="inline-flex items-center gap-2 text-xs bg-gold/10 border border-gold/30 text-forest rounded-full px-3 py-1.5">
                {DAY_LABELS[s.weekday]} {s.local_time}
                <button type="button" onClick={() => removeSlot(i)} className="text-clay hover:text-clay/70"><Trash2 className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        </div>

        <div className="border-2 border-gold/40 rounded-2xl p-5">
          <p className="text-[10px] uppercase tracking-widest text-gold mb-2"><Tx>Recitation test (required)</Tx></p>
          <p className="text-xs text-mute mb-3">
            <Tx>Please recite Surah Al-Fatiha or a short passage. Management reviews it privately before approving you — after approval it becomes a public sample students can listen to before booking.</Tx>
          </p>
          <div className="text-center py-2">
            {!recording && !audioBlob && (
              <button type="button" data-testid="apply-record" onClick={startRec}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-forest text-sand hover:bg-sage font-medium text-sm">
                <Mic className="w-4 h-4" /> <Tx>Start recording</Tx></button>)}
            {recording && (
              <button type="button" data-testid="apply-stop" onClick={stopRec}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-clay text-sand animate-pulse font-medium text-sm">
                <Square className="w-4 h-4 fill-current" /> <Tx>Stop</Tx></button>)}
            {audioBlob && !recording && (
              <div className="space-y-2">
                <audio src={audioUrl} controls className="mx-auto" />
                <button type="button" onClick={clearRec}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cream border border-forest/15 text-forest text-xs">
                  <RefreshCw className="w-3.5 h-3.5" /> <Tx>Re-record</Tx></button>
              </div>)}
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-2 text-sm text-clay bg-clay/10 rounded-2xl p-3">
            <AlertCircle className="w-4 h-4 mt-0.5" /> <span>{typeof error === "string" ? error : JSON.stringify(error)}</span>
          </div>)}

        <button data-testid="apply-submit" type="submit" disabled={busy || !audioBlob}
          className="w-full py-3 bg-forest text-sand hover:bg-sage rounded-full font-medium disabled:opacity-60 transition">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : <Tx>Submit application</Tx>}
        </button>
        <style>{`.input{width:100%;margin-top:.375rem;padding:.625rem 1rem;border-radius:9999px;background:#f6f0e3;border:1px solid rgba(46,90,71,.10);outline:none;font-size:.875rem;transition:border-color .2s}.input:focus{border-color:#2E5A47}textarea.input{border-radius:1rem}`}</style>
      </form>
    </div>
  );
};

export default TutorApply;
